/**
 * WebSocket Hook for Real-Time Updates
 *
 * Connects to the Scheduler Durable Object via WebSocket.
 * Subscribes to channels and auto-refreshes React Query caches
 * when schedule changes are received.
 *
 * Per CLAUDE.md WebSocket Protocol:
 * - Uses ticket-based authentication (30-second single-use tickets)
 * - Implements exponential backoff with jitter for reconnection
 * - Handles ping/pong heartbeat
 */

import { useEffect, useRef, useCallback, useState, createContext, useContext, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';

// API base URL (same as api.ts)
const API_BASE_URL =
  import.meta.env.VITE_API_URL ??
  (import.meta.env.DEV ? 'http://localhost:8787' : '');

interface WebSocketMessage {
  type: string;
  cursor?: string;
  timestamp?: string;
  scheduleVersion?: number;
  affectedProjects?: string[];
  skippedProjectIds?: string[];
  orderId?: string;
  alertId?: string;
  [key: string]: unknown;
}

interface WebSocketState {
  connected: boolean;
  lastMessage: WebSocketMessage | null;
  lastUpdateTime: Date | null;
  scheduleVersion: number | null;
}

interface WebSocketContextValue extends WebSocketState {
  reconnect: () => void;
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

/**
 * Provider component that establishes and manages the WebSocket connection.
 */
export function WebSocketProvider({
  channels,
  children,
}: {
  channels: string[];
  children: ReactNode;
}) {
  const wsRef = useRef<WebSocket | null>(null);
  const queryClient = useQueryClient();
  const [state, setState] = useState<WebSocketState>({
    connected: false,
    lastMessage: null,
    lastUpdateTime: null,
    scheduleVersion: null,
  });
  const reconnectTimeoutRef = useRef<number>();
  const reconnectAttempts = useRef(0);
  const channelsRef = useRef(channels);

  // Update channels ref when channels change
  useEffect(() => {
    channelsRef.current = channels;
  }, [channels]);

  const connect = useCallback(async () => {
    // Don't connect if already connected or connecting
    if (wsRef.current?.readyState === WebSocket.CONNECTING ||
        wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    try {
      // Get WebSocket ticket from API
      const ticketUrl = new URL('/api/v1/ws-ticket', API_BASE_URL);
      const ticketRes = await fetch(ticketUrl.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Mock-User-Role': 'leadership', // Development mock
        },
        credentials: 'include',
      });

      if (!ticketRes.ok) {
        console.warn('Failed to get WebSocket ticket:', ticketRes.status);
        // Schedule retry
        scheduleReconnect();
        return;
      }

      const { data } = await ticketRes.json();
      const ticket = data?.ticket;

      if (!ticket) {
        console.warn('No ticket in response');
        scheduleReconnect();
        return;
      }

      // Build WebSocket URL
      const wsProtocol = API_BASE_URL.startsWith('https') ? 'wss:' : 'ws:';
      const wsHost = API_BASE_URL.replace(/^https?:\/\//, '') || window.location.host;
      const wsUrl = `${wsProtocol}//${wsHost}/ws?ticket=${ticket}`;

      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('WebSocket connected');
        setState(prev => ({ ...prev, connected: true }));
        reconnectAttempts.current = 0;

        // Subscribe to channels
        channelsRef.current.forEach(channel => {
          ws.send(JSON.stringify({ type: 'subscribe', channel }));
        });
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data) as WebSocketMessage;

          // Handle different message types
          switch (message.type) {
            case 'hello':
              console.log('WebSocket hello:', message);
              break;

            case 'subscribed':
              console.log('Subscribed to channel:', message.channel);
              break;

            case 'schedule_updated':
              console.log('Schedule updated:', message);
              setState(prev => ({
                ...prev,
                lastMessage: message,
                lastUpdateTime: new Date(),
                scheduleVersion: message.scheduleVersion ?? prev.scheduleVersion,
              }));
              // Invalidate relevant queries
              queryClient.invalidateQueries({ queryKey: ['projects'] });
              queryClient.invalidateQueries({ queryKey: ['capacity'] });
              queryClient.invalidateQueries({ queryKey: ['at-risk'] });
              queryClient.invalidateQueries({ queryKey: ['orders'] });
              break;

            case 'order_updated':
              console.log('Order updated:', message.orderId);
              queryClient.invalidateQueries({ queryKey: ['orders'] });
              if (message.orderId) {
                queryClient.invalidateQueries({ queryKey: ['order', message.orderId] });
              }
              break;

            case 'alert_created':
              console.log('Alert created');
              queryClient.invalidateQueries({ queryKey: ['alerts'] });
              break;

            case 'ping':
              ws.send(JSON.stringify({ type: 'pong' }));
              break;

            case 'error':
              console.error('WebSocket error:', message);
              break;

            case 'cursor_expired':
              console.log('Cursor expired, refetching all data');
              queryClient.invalidateQueries();
              break;

            default:
              // Unknown message type - just update lastMessage
              setState(prev => ({ ...prev, lastMessage: message }));
          }
        } catch (err) {
          console.error('Failed to parse WebSocket message:', err);
        }
      };

      ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        setState(prev => ({ ...prev, connected: false }));
        wsRef.current = null;

        // Don't reconnect if closed normally (code 1000) or due to session revocation (4001)
        if (event.code !== 1000 && event.code !== 4001) {
          scheduleReconnect();
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        ws.close();
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('WebSocket connection failed:', error);
      scheduleReconnect();
    }
  }, [queryClient]);

  const scheduleReconnect = useCallback(() => {
    // Clear any existing timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    // Exponential backoff with jitter (per CLAUDE.md Client Reconnection Strategy)
    const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
    const jitter = Math.random() * 1000;
    reconnectAttempts.current++;

    // Max 10 retries before giving up
    if (reconnectAttempts.current < 10) {
      console.log(`Scheduling reconnect in ${Math.round(delay + jitter)}ms (attempt ${reconnectAttempts.current})`);
      reconnectTimeoutRef.current = window.setTimeout(connect, delay + jitter);
    } else {
      console.warn('Max reconnect attempts reached');
    }
  }, [connect]);

  const reconnect = useCallback(() => {
    // Force reconnect
    if (wsRef.current) {
      wsRef.current.close();
    }
    reconnectAttempts.current = 0;
    connect();
  }, [connect]);

  // Connect on mount
  useEffect(() => {
    connect();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting');
      }
    };
  }, [connect]);

  const contextValue: WebSocketContextValue = {
    ...state,
    reconnect,
  };

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
}

/**
 * Hook to access WebSocket state and controls.
 */
export function useWebSocket(): WebSocketContextValue {
  const context = useContext(WebSocketContext);

  if (!context) {
    // Return a default state if not within provider (for components that might render outside)
    return {
      connected: false,
      lastMessage: null,
      lastUpdateTime: null,
      scheduleVersion: null,
      reconnect: () => {},
    };
  }

  return context;
}

/**
 * Hook that just returns connection status.
 */
export function useWebSocketStatus() {
  const { connected, lastUpdateTime, scheduleVersion } = useWebSocket();
  return { connected, lastUpdateTime, scheduleVersion };
}
